/**
 * ChatPanel — runtime chat harness selector (Plan E Phase 3 v1.1.0)
 *
 * Reads the user's harness selection from `useChatHarnessStore` (per-tenant
 * localStorage). Default = `legacy`, preserving existing behavior. Users
 * opt into Pi mode (or hide chat via `none`) through the SettingsMenu
 * dropdown. Replaces the build-time `VITE_USE_PI_CHAT` toggle previously
 * here — that mechanism is gone.
 *
 * `ChatPanelPi` is lazy-imported so the legacy + none paths don't pull in
 * `@earendil-works/pi-web-ui` + pdfjs-dist + DOMMatrix bundle weight.
 */
import { lazy, Suspense, useEffect } from 'react';
import ChatPanelLegacy from './ChatPanelLegacy';
import { useChatHarnessStore } from '@/stores/chatHarnessStore';
import { useTenantStore } from '@/stores/tenantStore';

const LazyChatPanelPi = lazy(() =>
  import('./ChatPanelPi').then((m) => ({ default: m.ChatPanelPi })),
);

function ChatPanelPiWithSuspense() {
  return (
    <Suspense fallback={<div className="p-4 text-sm text-vibe-text-secondary">Loading Pi chat…</div>}>
      <LazyChatPanelPi />
    </Suspense>
  );
}

function ChatPanelNone() {
  return (
    <div className="p-4 text-sm text-vibe-text-secondary text-center">
      Chat harness disabled. Open the gear menu to choose a harness.
    </div>
  );
}

export function ChatPanel() {
  const harness = useChatHarnessStore((s) => s.harness);
  const hydrate = useChatHarnessStore((s) => s.hydrate);
  const tenantId = useTenantStore((s) => s.tenantId);

  useEffect(() => {
    hydrate(tenantId);
  }, [tenantId, hydrate]);

  if (harness === 'pi') return <ChatPanelPiWithSuspense />;
  if (harness === 'none') return <ChatPanelNone />;
  return <ChatPanelLegacy />;
}

export default ChatPanel;
